import { observer } from 'mobx-react-lite'
import { GetServerSideProps } from 'next'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useState } from 'react'
import { SEOHead } from '~/components/business/SEOHead'
import { GlobalRequest } from '~/components/global-requset'
import ActivityStatus from '~/components/pages/presale/ActivityStatus'
import BuyBtn from '~/components/pages/presale/BuyBtn'
import Guideline from '~/components/pages/presale/Guideline'
import OrderModal from '~/components/pages/presale/OrderModal'
// import { MyProgress } from '~/components/pages/presale/Progress'
import { MyProgress } from '~/components/ui/MyProgress'
import { Container } from '~/components/ui/Container'
import { MyImage } from '~/components/ui/MyImage'
import { MyModal } from '~/components/ui/MyModal'
import { GET_PRE_SALE_ACTIVITY } from '~/graphqls/gqls/presale'
import {
  CopyrightUserRole,
  CrowdfundingActivityStatusEnum,
  getPreSaleActivity,
  getPreSaleActivityVariables,
  getPreSaleActivity_marketingModule_crowdfundingActivity,
} from '~/graphqls/types'
import { useImperativeQuery } from '~/hooks/useImperativeQuery'
import { useValidateAuth } from '~/hooks/useValidateAuth'
import { Layout } from '~/layouts/default'
import { FenToYuan, toHtml, toYMDHms } from '~/utils'
import {
  ownerNote,
  useNote,
  adaptNote,
  presaleBuyNote,
  presaleOwnerNote,
  buyNote,
} from '~/configs/text'
import { round } from 'lodash'
import { VipIcon } from '~/components/icon/Vip'

interface Props {
  remoteData: getPreSaleActivity_marketingModule_crowdfundingActivity
}

const TEXT_LIMIT = 145

const CrowdFunding = ({ remoteData }: Props) => {
  const [data, setData] = useState(remoteData)
  const [orderVisible, setOrderVisible] = useState(false)
  const [descModalVisible, setDescModalVisible] = useState(false)

  const { validateIsLogin, validateIsRealName, validateBindBankCard } =
    useValidateAuth()
  const onBuy = () => {
    if (validateIsLogin() && validateIsRealName() && validateBindBankCard()) {
      setOrderVisible(true)
    }
  }

  const queryAction = useImperativeQuery<
    getPreSaleActivity,
    getPreSaleActivityVariables
  >(GET_PRE_SALE_ACTIVITY)
  const router = useRouter()
  const refreshData = () => {
    queryAction({ id: router.query.id })
      .then(res => {
        const newData = res?.data?.marketingModule?.crowdfundingActivity
        setData(newData!)
      })
      .catch(err => {
        console.error(err)
      })
  }

  const publicPercent =
    Number(
      (data.crowdfunding?.totalSoldCount as number) /
        (data.crowdfunding?.count as number)
    ) || 0

  const presalePrecent =
    Number(
      (data.crowdfunding?.soldAmount as number) /
        (data.crowdfunding?.goalAmount as number)
    ) || 0

  return (
    <Layout>
      <SEOHead title={data.name} />
      <Container>
        <div className="flex items-start">
          <MyImage
            tag="v400"
            style={{
              width: '640px',
              height: '640px',
              flexShrink: 0,
              borderRadius: '22px',
              marginRight: '48px',
            }}
            src={data.crowdfunding?.primaryImage}
          />
          <div className="flex-1">
            <div className="text-neutral-50 bg-[#F9DEB9] rounded-full px-3 inline-block py-1">
              <span>限量</span>
              <span className="font-semibold ml-1.5">
                {data.crowdfunding?.count}
              </span>
            </div>
            <div className="text-xl my-4 font-semibold">
              {data.crowdfunding?.name}
            </div>
            <Link href={`/user/${data.crowdfunding?.artist?.uid}`}>
              <a className="flex items-center">
                <div className="relative w-8 h-8">
                  <MyImage
                    tag="v400"
                    className="w-full h-full rounded-[50%]"
                    src={data.crowdfunding?.artist?.avatarUrl}
                  />
                  <VipIcon
                    userRole={data.crowdfunding?.artist?.userRoleInfo}
                    className="h-3 -right-[3px]  max-w-[15px]"
                  />
                </div>
                <div className="ml-2 text-sm font-semibold">
                  {data.crowdfunding?.artist?.nickname}
                </div>
              </a>
            </Link>

            <div className="text-base text-neutral-400 my-4">
              <span className="mr-4">
                预售价格
                <span className="text-error">
                  ￥{FenToYuan(data.crowdfunding?.price)}
                </span>
              </span>
              <span>
                公售价格
                <span className="text-error">
                  ￥{FenToYuan(data.crowdfunding?.publicPrice)}
                </span>
              </span>
            </div>

            <div className="text-sm text-neutral-400 break-words">
              <span>
                {(data.crowdfunding?.description || '').substring(
                  0,
                  TEXT_LIMIT
                )}
              </span>
              {(data.crowdfunding?.description || '').length > TEXT_LIMIT && (
                <>
                  ...
                  <span
                    className="font-semibold text-xs text-neutral-600 ml-2 cursor-pointer"
                    onClick={() => setDescModalVisible(true)}
                  >
                    查看更多
                  </span>
                </>
              )}
            </div>

            <div className="my-8">
              {[
                CrowdfundingActivityStatusEnum.PUBLIC,
                CrowdfundingActivityStatusEnum.END,
              ].includes(data.status as CrowdfundingActivityStatusEnum) ? (
                <MyProgress
                  percent={Number((publicPercent * 100).toFixed(2))}
                  format={val => `剩余 ${round(100 - val)}%`}
                  className="progress-primary"
                />
              ) : (
                <MyProgress
                  percent={round(presalePrecent * 100)}
                  max={Infinity}
                  format={val => `${val}%`}
                  className="progress-primary"
                />
              )}
            </div>

            <div className="flex items-center justify-between gap-2 my-8 font-semibold">
              <div className="bg-neutral-700 rounded-lg px-4 flex flex-col items-center justify-center flex-1 h-16">
                <div className="text-error text-base">
                  ￥{FenToYuan(data.crowdfunding?.soldAmount)}
                </div>
                <div className="text-xs text-neutral-400">
                  {data.crowdfunding?.soldCount}人支持
                </div>
              </div>
              <div className="bg-neutral-700 rounded-lg px-4 flex flex-col items-center justify-center flex-1 h-16">
                <div className="text-error text-base">
                  ￥{FenToYuan(data.crowdfunding?.goalAmount)}
                </div>
                <div className="text-xs text-neutral-400">预售目标</div>
              </div>

              <div className="bg-neutral-700 rounded-lg flex flex-col  items-center justify-center flex-1 h-16 px-4">
                <ActivityStatus
                  remoteData={data}
                  status={data.status}
                  countdownCallback={refreshData}
                />
              </div>
            </div>

            <div className="rounded-2xl border-2 border-solid border-neutral-600 p-6 flex flex-col items-start my-8">
              <div>
                <span className="text-neutral-400 ">预售截止时间</span>
                <span className="text-neutral-50 ml-1.5 font-semibold">
                  {toYMDHms(data.crowdfunding?.endTime)}
                </span>
              </div>
              <div className="mt-1">
                <span className="text-neutral-400 ">发放截止时间</span>
                <span className="text-neutral-50 ml-1.5 font-semibold">
                  {toYMDHms(data.crowdfunding?.postEndTime)}
                </span>
              </div>
            </div>

            <BuyBtn
              status={data.status!}
              buyStatus={data.buyStatus!}
              onClick={onBuy}
            />
          </div>
        </div>
        <div className="mt-14">
          <Guideline
            buyNote={
              [
                CrowdfundingActivityStatusEnum.PUBLIC,
                CrowdfundingActivityStatusEnum.END,
              ].includes(data.status as CrowdfundingActivityStatusEnum)
                ? buyNote
                : presaleBuyNote
            }
            ownerNote={
              [
                CrowdfundingActivityStatusEnum.PUBLIC,
                CrowdfundingActivityStatusEnum.END,
              ].includes(data.status as CrowdfundingActivityStatusEnum)
                ? `一、版权所有权权益须知：
                ${ownerNote}

                二、版权使用权权益须知：
                ${useNote}
                
                三、版权改编权权益须知：
                ${adaptNote}
                `
                : presaleOwnerNote
            }
          />
        </div>

        <div className="mt-6">
          <h2 className="text-lg font-semibold">IP 详情</h2>
          <div className="border-2 border-solid border-neutral-600 rounded-2xl p-6">
            {data.crowdfunding?.conceptImage?.map(item => {
              return (
                <img
                  key={item?.id}
                  src={item?.artworkUrl || ''}
                  alt=""
                  className="block mx-auto"
                />
              )
            })}
          </div>
        </div>
      </Container>

      {orderVisible && (
        <OrderModal
          visible={orderVisible}
          setVisible={setOrderVisible}
          product={{
            image: data.crowdfunding?.primaryImage || '',
            name: data.crowdfunding?.name || '',
            buyType: data.crowdfunding?.buyType?.buyType!,
            price: data.crowdfunding?.buyType?.price,
            seriesId: data.crowdfunding?.buyType?.artworkSeriesId!,
            endTime: data.crowdfunding?.postEndTime,
            hasProductNow:
              data.status === CrowdfundingActivityStatusEnum.PUBLIC,
          }}
        />
      )}

      {descModalVisible && (
        <MyModal
          visible={descModalVisible}
          title="预售介绍"
          onCancel={() => setDescModalVisible(false)}
          maskStyle={{
            backgroundColor: 'rgba(0,0,0,0.8)',
          }}
          width={704}
          footer={null}
        >
          <div
            className="text-neutral-400 text-base"
            dangerouslySetInnerHTML={{
              __html: toHtml(data.crowdfunding?.description || ''),
            }}
          ></div>
        </MyModal>
      )}
    </Layout>
  )
}

export default observer(CrowdFunding)

export const getServerSideProps: GetServerSideProps = async context => {
  const { initialState, client } = await GlobalRequest(context)
  const id = Number(context.query.id)

  const res = await client.query<
    getPreSaleActivity,
    getPreSaleActivityVariables
  >({
    query: GET_PRE_SALE_ACTIVITY,
    variables: {
      id,
    },
    fetchPolicy: 'no-cache',
  })

  return {
    props: {
      initialState,
      remoteData: res?.data?.marketingModule?.crowdfundingActivity,
    },
  }
}
